// packages/report-components/src/RecommendationList.tsx
//
// ADR-0018 Fase 7 — action-plan cards. Upgrades the old numbered <ol>
// into an editorial "Action plan — N moves" block:
//
//   - Mini-hero heading in display-font ("Action plan — 4 moves")
//   - Each Recommendation renders as a card with numbered circle, title,
//     priority badge, body, and a meta-row for optional expectedImpact /
//     owner / dueBy
//   - Optional `evidenceSectionId`: renders an "Evidence →" text-link
//     that jumps to the linked section (same mechanism as DataPoint's
//     evidence click). Validated at render time against the surface's
//     `hasSection` (JumpContext) — unknown ids, and surfaces with no
//     outline at all, render no link (no dead-click affordance).
//   - Card hover-lift handled in components.css (respects
//     prefers-reduced-motion)
//
// Backwards compatibility:
//   - `Recommendation` keeps `title`, `body`, `priority` as-is
//   - New props are optional; pre-redesign items render without the
//     meta row or evidence link
//   - Default heading auto-swaps to "Action plan — N moves" when
//     items.length > 0; caller can still override via the `heading` prop

import * as React from "react";

import { useJump } from "./JumpContext";
import {
  PageListCard,
  PageListItem,
  PageMetaLine,
  pageListBodyLines,
} from "./KeyTakeaways";
import { usePageDensity, type PageDensity } from "./PageDensityContext";

export type RecommendationPriority = "high" | "medium" | "low";

export type Recommendation = {
  /** Short imperative title ("Shift budget to top ROAS campaigns"). */
  title: string;
  /** One-or-two sentence rationale + action. */
  body: string;
  priority?: RecommendationPriority;
  /** ADR-0018 Fase 7 — section id the recommendation leans on. When the
   * surface knows the id, an "Evidence →" link renders that jumps to the
   * target. Unknown id → no link. */
  evidenceSectionId?: string;
  /** Quantified upside/downside when the data supports it ("+€8.2K
   * projected monthly upside"). Prompt rule: omit when not grounded. */
  expectedImpact?: string;
  /** Optional deliverable ownership. LLM may leave blank. */
  owner?: string;
  /** Optional deliverable due date, any human-readable string
   * ("by April 30" / "Q2-wk3"). LLM may leave blank. */
  dueBy?: string;
};

export type RecommendationListProps = {
  items: Recommendation[];
  /** Override the heading — defaults to `Action plan — N moves`. */
  heading?: string;
  /** ON A REPORT PAGE only: how many lines of body every item reserves. The
   * server priced the cell with the same number (`page_list_item_px`), so it
   * is data the adapter hands down. Clamped to 1..`list_max_body_lines`;
   * ignored off a page, where the card flows. */
  bodyLines?: number;
};

function priorityColours(priority: RecommendationPriority | undefined): {
  bg: string;
  fg: string;
  label: string;
} {
  if (priority === "high") {
    return {
      bg: "var(--verity-red-0, #fff5f5)",
      fg: "var(--verity-red-7, #f03e3e)",
      label: "High priority",
    };
  }
  if (priority === "low") {
    return {
      bg: "var(--verity-gray-1, #F3F4F6)",
      fg: "var(--verity-gray-7, #374151)",
      label: "Low priority",
    };
  }
  // Default to medium — LLM may omit the field.
  return {
    bg: "var(--verity-blue-0, #EEF2FC)",
    fg: "var(--verity-blue-7, #2B47B3)",
    label: "Medium priority",
  };
}

function RecommendationNumber({ n }: { n: number }): React.ReactElement {
  return (
    <span
      aria-hidden="true"
      style={{
        flex: "0 0 auto",
        width: 32,
        height: 32,
        borderRadius: "var(--verity-radius-full, 9999px)",
        background: "var(--verity-color-primary, #3455D3)",
        color: "var(--verity-color-primary-fg, #ffffff)",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: 14,
        fontWeight: 700,
        lineHeight: 1,
      }}
    >
      {n}
    </span>
  );
}

function PriorityBadge({
  priority,
}: {
  priority?: RecommendationPriority;
}): React.ReactElement | null {
  if (!priority) return null;
  const { bg, fg, label } = priorityColours(priority);
  return (
    <span
      style={{
        display: "inline-block",
        padding: "2px 8px",
        borderRadius: "var(--verity-radius-sm, 6px)",
        background: bg,
        color: fg,
        fontSize: 11,
        fontWeight: 600,
        letterSpacing: "0.02em",
      }}
    >
      {label}
    </span>
  );
}

/** Evidence link — renders only when the surface confirms the target
 * exists. Hook call at top-level of a component (not inside a map loop
 * condition) — this sub-component is rendered once per card so the
 * hook rule holds. */
function EvidenceLink({
  sectionId,
  compact = false,
  leading,
}: {
  sectionId: string;
  /** Page form: the link shares one clamped line with the rest of the meta,
   * so it takes the line's own type and sits on its baseline. */
  compact?: boolean;
  /** Drawn immediately before the button, and only when the button itself
   * draws. The separator belongs to the link because only the link knows
   * whether the surface confirmed the target. */
  leading?: React.ReactNode;
}): React.ReactElement | null {
  const { onJump, hasSection } = useJump();
  if (!hasSection(sectionId)) return null;
  const button = (
    <button
      type="button"
      className="verity-recommendation-card__evidence"
      onClick={() => onJump(sectionId)}
      style={{
        border: 0,
        background: "transparent",
        padding: 0,
        cursor: "pointer",
        font: "inherit",
        color: "var(--verity-color-primary, #3455D3)",
        fontSize: compact ? "inherit" : "var(--verity-font-size-sm, 13px)",
        lineHeight: compact ? "inherit" : undefined,
        verticalAlign: compact ? "baseline" : undefined,
        fontWeight: 600,
      }}
    >
      Evidence →
    </button>
  );
  if (!leading) return button;
  return (
    <>
      {leading}
      {button}
    </>
  );
}

// ---------------------------------------------------------------------------
// The page form
// ---------------------------------------------------------------------------
//
// On a report page a recommendation is drawn as a TAKEAWAY: the same card, the
// same 24px numeral, the same hairline between items, no card per item (plan
// 2026-09-19-a-report-page-has-a-house-style, A4, the product owner's call).
// The reason is arithmetic as much as style: a recommendation card measured 76
// to 98px against the 45px the server budgeted, so the second item of a block
// fell off the page (symptom 7). The boxes come from `KeyTakeaways`, which owns
// the page list form; what goes in them is this file's business.
//
// Off a page nothing changes: `usePageDensity()` is null in the flowing report,
// in the mail and on the dashboard, and the action-plan cards below draw
// exactly as they always did.

/** A middle dot between two parts of the one meta line. */
function MetaSeparator(): React.ReactElement {
  return <span style={{ opacity: 0.4, margin: "0 6px" }}>·</span>;
}

/** The priority as it fits ON the meta line: a badge sized to sit inside one
 * body line rather than to start one of its own. */
function PagePriorityBadge({
  priority,
}: {
  priority?: RecommendationPriority;
}): React.ReactElement | null {
  if (!priority) return null;
  const { bg, fg, label } = priorityColours(priority);
  return (
    <span
      style={{
        display: "inline-block",
        height: 16,
        lineHeight: "16px",
        padding: "0 6px",
        verticalAlign: "middle",
        borderRadius: "var(--verity-radius-sm, 6px)",
        background: bg,
        color: fg,
        fontSize: 11,
        fontWeight: 600,
        letterSpacing: "0.02em",
      }}
    >
      {label}
    </span>
  );
}

/** Does this item carry a meta line on a page?
 *
 * Read from the DATA, not from what ends up drawn: an evidence id the surface
 * cannot resolve still costs the line, because the server budgeted the item
 * from the same fields and a block must not be one item shorter in the reading
 * room than in the PDF. */
function hasPageMeta(item: Recommendation): boolean {
  return Boolean(
    item.priority ||
      (item.expectedImpact && item.expectedImpact.trim().length > 0) ||
      (item.owner && item.owner.trim().length > 0) ||
      (item.dueBy && item.dueBy.trim().length > 0) ||
      (typeof item.evidenceSectionId === "string" && item.evidenceSectionId.length > 0),
  );
}

function PageRecommendation({
  density,
  item,
  index,
  last,
  bodyLines,
}: {
  density: PageDensity;
  item: Recommendation;
  index: number;
  last: boolean;
  bodyLines: number;
}): React.ReactElement {
  const parts: React.ReactNode[] = [];
  const push = (node: React.ReactNode, key: string) => {
    if (parts.length > 0) parts.push(<MetaSeparator key={`sep-${key}`} />);
    parts.push(<React.Fragment key={key}>{node}</React.Fragment>);
  };
  if (item.priority) push(<PagePriorityBadge priority={item.priority} />, "priority");
  if (item.expectedImpact && item.expectedImpact.trim().length > 0) {
    push(
      <>
        <span style={{ opacity: 0.7 }}>Impact </span>
        <span style={{ color: "var(--verity-color-ink, var(--verity-color-text, #111827))", fontWeight: 500 }}>
          {item.expectedImpact}
        </span>
      </>,
      "impact",
    );
  }
  if (item.owner && item.owner.trim().length > 0) {
    push(
      <>
        <span style={{ opacity: 0.7 }}>Owner </span>
        <span>{item.owner}</span>
      </>,
      "owner",
    );
  }
  if (item.dueBy && item.dueBy.trim().length > 0) {
    push(
      <>
        <span style={{ opacity: 0.7 }}>Due </span>
        <span>{item.dueBy}</span>
      </>,
      "due",
    );
  }
  const evidence =
    typeof item.evidenceSectionId === "string" && item.evidenceSectionId.length > 0 ? (
      <EvidenceLink
        key="evidence"
        sectionId={item.evidenceSectionId}
        compact
        leading={parts.length > 0 ? <MetaSeparator /> : undefined}
      />
    ) : null;
  const meta = hasPageMeta(item) ? (
    <PageMetaLine density={density} className="verity-recommendation-list__meta">
      {parts}
      {evidence}
    </PageMetaLine>
  ) : undefined;

  return (
    <PageListItem
      density={density}
      n={index + 1}
      last={last}
      bodyLines={bodyLines}
      className="verity-recommendation-list__item"
      title={item.title}
      body={item.body}
      meta={meta}
    />
  );
}

function RecommendationCard({
  item,
  index,
}: {
  item: Recommendation;
  index: number;
}): React.ReactElement {
  const hasMeta =
    (item.expectedImpact && item.expectedImpact.trim().length > 0) ||
    (item.owner && item.owner.trim().length > 0) ||
    (item.dueBy && item.dueBy.trim().length > 0) ||
    (typeof item.evidenceSectionId === "string" &&
      item.evidenceSectionId.length > 0);

  return (
    <li
      className="verity-recommendation-card"
      style={{
        listStyle: "none",
        display: "flex",
        gap: "var(--verity-space-md, 16px)",
        alignItems: "flex-start",
        padding: "var(--verity-space-md, 16px)",
        borderRadius: "var(--verity-radius-md, 8px)",
        border:
          "1px solid var(--verity-color-border-subtle, rgba(0,0,0,0.06))",
        background: "var(--verity-color-surface, #ffffff)",
      }}
    >
      <RecommendationNumber n={index + 1} />
      <div style={{ flex: "1 1 auto", minWidth: 0 }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            flexWrap: "wrap",
            marginBottom: 4,
          }}
        >
          <strong
            style={{
              fontSize: "var(--verity-font-size-md, 15px)",
              fontWeight: 600,
              color: "var(--verity-color-ink, var(--verity-color-text, #111827))",
            }}
          >
            {item.title}
          </strong>
          <PriorityBadge priority={item.priority} />
        </div>
        <p
          style={{
            margin: 0,
            fontSize: "var(--verity-font-size-sm, 13px)",
            lineHeight: "var(--verity-line-height-relaxed, 1.7)",
            color: "var(--verity-color-text, #111827)",
          }}
        >
          {item.body}
        </p>
        {hasMeta ? (
          <div
            className="verity-recommendation-card__meta"
            style={{
              marginTop: 12,
              display: "flex",
              flexWrap: "wrap",
              gap: "8px 16px",
              alignItems: "center",
              fontSize: "var(--verity-font-size-xs, 12px)",
              color: "var(--verity-color-text-muted, #6b7280)",
            }}
          >
            {item.expectedImpact ? (
              <span>
                <span style={{ opacity: 0.7 }}>Impact · </span>
                <span
                  style={{
                    color:
                      "var(--verity-color-ink, var(--verity-color-text, #111827))",
                    fontWeight: 500,
                  }}
                >
                  {item.expectedImpact}
                </span>
              </span>
            ) : null}
            {item.owner ? (
              <span>
                <span style={{ opacity: 0.7 }}>Owner · </span>
                <span>{item.owner}</span>
              </span>
            ) : null}
            {item.dueBy ? (
              <span>
                <span style={{ opacity: 0.7 }}>Due · </span>
                <span>{item.dueBy}</span>
              </span>
            ) : null}
            {item.evidenceSectionId ? (
              <EvidenceLink sectionId={item.evidenceSectionId} />
            ) : null}
          </div>
        ) : null}
      </div>
    </li>
  );
}

export function RecommendationList({
  items,
  heading,
  bodyLines,
}: RecommendationListProps): React.ReactElement | null {
  // Non-null only on a report PAGE. See "The page form" above.
  const density = usePageDensity();
  if (!Array.isArray(items) || items.length === 0) {
    return null;
  }
  const resolvedHeading =
    heading && heading.trim().length > 0
      ? heading
      : `Action plan — ${items.length} ${items.length === 1 ? "move" : "moves"}`;

  if (density) {
    const lines = pageListBodyLines(density, bodyLines);
    return (
      <PageListCard
        density={density}
        heading={resolvedHeading}
        className="verity-recommendation-list"
      >
        {items.map((item, i) => (
          <PageRecommendation
            key={i}
            density={density}
            item={item}
            index={i}
            last={i === items.length - 1}
            bodyLines={lines}
          />
        ))}
      </PageListCard>
    );
  }

  return (
    <section
      className="verity-recommendation-list"
      style={{
        marginTop: "var(--verity-space-xl, 40px)",
        paddingTop: "var(--verity-space-lg, 24px)",
        borderTop:
          "1px solid var(--verity-color-border-subtle, rgba(0,0,0,0.06))",
      }}
    >
      <h2
        style={{
          margin: 0,
          marginBottom: "var(--verity-space-md, 16px)",
          fontFamily:
            "var(--verity-font-display, ui-serif, Georgia, serif)",
          fontSize: "clamp(22px, 2.4vw, 30px)",
          fontWeight: 600,
          letterSpacing: "-0.02em",
          color:
            "var(--verity-color-ink, var(--verity-color-text, #111827))",
        }}
      >
        {resolvedHeading}
      </h2>
      <ol
        style={{
          listStyle: "none",
          padding: 0,
          margin: 0,
          display: "flex",
          flexDirection: "column",
          gap: "var(--verity-space-md, 16px)",
        }}
      >
        {items.map((item, i) => (
          <RecommendationCard key={i} item={item} index={i} />
        ))}
      </ol>
    </section>
  );
}
